import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Zap, Image, Code2, CheckCircle2, AlertTriangle, Copy,
  ChevronDown, ChevronUp, Play, Square,
  RefreshCw, Sparkles, Info, ArrowRight,
  FileCode2, Rocket, Wrench, Package, Eye,
  Loader2, TrendingUp, Clock, BarChart2,
} from "lucide-react";

/* ── helpers ── */
const fmtBytes = (n) => {
  if (!n) return "0 B";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / 1024 / 1024).toFixed(2) + " MB";
};

/* ── Script Panel ── */
function ScriptPanel({ type, title, description }) {
  const [script, setScript] = useState(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [guide, setGuide] = useState([]);

  const load = async () => {
    if (script) { setOpen(!open); return; }
    try {
      const { data } = await api.get(`/optimizer/scripts/${type}`);
      setScript(data.script);
      setGuide(data.install_guide || []);
      setOpen(true);
    } catch { toast.error("فشل تحميل السكربت"); }
  };

  const copy = () => {
    if (!script) return;
    const fallback = () => {
      const ta = document.createElement("textarea");
      ta.value = script; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      setCopied(true); toast.success("تم نسخ الكود!");
      setTimeout(() => setCopied(false), 2500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(script)
        .then(() => { setCopied(true); toast.success("تم نسخ الكود!"); setTimeout(() => setCopied(false), 2500); })
        .catch(fallback);
    } else { fallback(); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-blue-200 transition-all" data-testid={`script-panel-${type}`}>
      <button onClick={load} className="w-full flex items-center gap-3 px-5 py-4 text-right hover:bg-[var(--bg-card-hover)] transition-colors">
        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center shrink-0 border border-blue-100">
          <FileCode2 className="w-4 h-4 text-[#004EEB]" />
        </div>
        <div className="flex-1 text-right">
          <div className="text-sm font-bold text-slate-800">{title}</div>
          <div className="text-xs text-slate-400 mt-0.5">{description}</div>
        </div>
        <span className="text-[9px] font-bold px-2 py-1 rounded-full bg-blue-50 text-[#004EEB] border border-blue-100 shrink-0">سكربت جاهز</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
      {open && script && (
        <div className="border-t border-slate-100">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">خطوات التركيب في سلة</div>
            <ol className="space-y-1">
              {guide.map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                  <span className="w-4 h-4 rounded-full bg-[#004EEB] text-white flex items-center justify-center text-[9px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
          <div className="relative">
            <pre className="p-4 text-[10px] text-slate-600 bg-slate-900/3 overflow-x-auto max-h-48 leading-relaxed font-mono" style={{ direction: "ltr" }}>{script}</pre>
            <button onClick={copy} data-testid={`copy-script-${type}`}
              className={`absolute top-2 left-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all
                ${copied ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-white text-slate-700 border border-slate-200 hover:border-[#004EEB] hover:text-[#004EEB]"}`}>
              {copied ? <><CheckCircle2 className="w-3 h-3" /> تم النسخ</> : <><Copy className="w-3 h-3" /> نسخ الكود</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Product Preview List (before optimization) ── */
function ProductPreviewList({ onConfirm, onClose, sallaConnected }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toOptimize, setToOptimize] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/optimizer/products-preview");
        setProducts(data.products || []);
        setToOptimize(data.to_optimize || 0);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "فشل تحميل المنتجات");
        onClose();
      } finally { setLoading(false); }
    };
    load();
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-lg" data-testid="product-preview-panel">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100" style={{ background: "#F8FAFC" }}>
        <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
          <Eye className="w-4 h-4 text-[#004EEB]" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-800">معاينة المنتجات المتأثرة</div>
          <div className="text-xs text-slate-400 mt-0.5">المنتجات التي ستُحسَّن صورها تلقائياً</div>
        </div>
        {!loading && (
          <div className="text-center px-3 py-1.5 rounded-xl shrink-0" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
            <div className="text-lg font-black text-[#004EEB]">{toOptimize}</div>
            <div className="text-[9px] text-blue-600 font-bold">منتج</div>
          </div>
        )}
      </div>

      {/* Product List */}
      <div className="max-h-80 overflow-y-auto">
        {loading && (
          <div className="flex flex-col items-center py-12 gap-3">
            <div className="w-8 h-8 border-2 border-[#004EEB] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-400">جاري تحميل المنتجات من سلة...</span>
          </div>
        )}
        {!loading && products.length === 0 && (
          <div className="text-center py-12 text-sm text-slate-400">لا توجد منتجات متاحة</div>
        )}
        {!loading && products.length > 0 && (
          <div className="divide-y divide-slate-50">
            {products.map((p, i) => (
              <div key={p.id || i} className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--bg-card-hover)]/60 transition-colors"
                data-testid={`preview-product-${i}`}>
                {/* Thumbnail */}
                <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-slate-100 bg-slate-50">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-4 h-4 text-slate-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-700 truncate">{p.name}</div>
                  {p.sku && <div className="text-[10px] text-slate-400">SKU: {p.sku}</div>}
                </div>

                {/* Status badge */}
                <div className={`text-[9px] font-bold px-2 py-1 rounded-full shrink-0 ${
                  p.will_optimize
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-slate-100 text-slate-500 border border-slate-200"
                }`}>
                  {p.will_optimize ? `توفير ${p.estimated_savings}` : p.estimated_savings}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {!loading && (
        <div className="flex items-center gap-3 px-5 py-4 border-t border-slate-100 bg-slate-50">
          <div className="flex-1 text-xs text-slate-500">
            {toOptimize > 0
              ? <><span className="font-bold text-slate-700">{toOptimize} صورة</span> ستُضغط وتُحوَّل لـ WebP</>
              : "جميع الصور محسّنة بالفعل (WebP)"}
          </div>
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-[var(--bg-card-hover)] transition-colors">
            إلغاء
          </button>
          {toOptimize > 0 && (
            <button onClick={onConfirm} data-testid="confirm-start-bulk-btn"
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-white rounded-lg transition-all hover:shadow-md hover:shadow-blue-200"
              style={{ background: "#004EEB" }}>
              <Rocket className="w-3.5 h-3.5" /> ابدأ التحسين ({toOptimize})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Live Product Card ── */
function LiveProductCard({ product }) {
  const statusConfig = {
    compressed: { color: "#10B981", bg: "#ECFDF5", border: "#A7F3D0", label: "تم الضغط", icon: CheckCircle2 },
    skipped:    { color: "#94A3B8", bg: "#F8FAFC", border: "#E2E8F0", label: "تم التخطي", icon: Info },
    error:      { color: "#EF4444", bg: "#FEF2F2", border: "#FECACA", label: "خطأ", icon: AlertTriangle },
    processing: { color: "#004EEB", bg: "#EFF6FF", border: "#BFDBFE", label: "معالجة...", icon: Loader2 },
  };
  const cfg = statusConfig[product.status] || statusConfig.processing;
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border transition-all"
      style={{ background: cfg.bg, borderColor: cfg.border }}
      data-testid={`live-product-${product.product_id}`}>
      <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0 border border-white bg-white">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-slate-50">
            <Image className="w-4 h-4 text-slate-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-slate-700 truncate">{product.name}</div>
        {product.status === "compressed" && product.savings_percent > 0 && (
          <div className="text-[10px] font-bold" style={{ color: "#10B981" }}>
            وُفِّر {product.savings_percent}% — {product.original_kb}KB → {product.optimized_kb}KB
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0 text-[10px] font-bold" style={{ color: cfg.color }}>
        <Icon className={`w-3.5 h-3.5 ${product.status === "processing" ? "animate-spin" : ""}`} />
        {cfg.label}
      </div>
    </div>
  );
}

/* ── Bulk Image Optimizer (main component) ── */
function BulkImageOptimizer({ sallaConnected }) {
  const [actions, setActions] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [starting, setStarting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const pollRef = useRef(null);

  const loadActions = useCallback(async () => {
    try {
      const { data } = await api.get("/optimizer/actions");
      setActions(data);
      if (data.active_job) {
        setJobId(data.active_job.id);
        setJobData(data.active_job);
      } else if (data.last_job) {
        setJobData(data.last_job);
        setJobId(data.last_job.id);
      }
    } catch {}
  }, []);

  useEffect(() => { loadActions(); }, [loadActions]);

  // Poll job status
  useEffect(() => {
    if (!jobId || !jobData) return;
    if (["completed", "failed", "cancelled"].includes(jobData?.status)) {
      clearInterval(pollRef.current); return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/optimizer/bulk-images/${jobId}`);
        setJobData(data);
        if (["completed", "failed", "cancelled"].includes(data.status)) {
          clearInterval(pollRef.current);
          if (data.status === "completed") toast.success(`اكتمل! ضُغطت ${data.compressed_count} صورة`);
        }
      } catch { clearInterval(pollRef.current); }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [jobId, jobData?.status]);

  const startJob = async () => {
    setStarting(true); setShowPreview(false);
    try {
      const { data } = await api.post("/optimizer/bulk-images/start");
      setJobId(data.job_id);
      setJobData({ ...data, status: "starting", progress: 0, live_products: [] });
      toast.success("بدأ محرك النمو! جارٍ معالجة الصور...");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل بدء التحسين");
    } finally { setStarting(false); }
  };

  const cancelJob = async () => {
    if (!jobId) return;
    try {
      await api.post(`/optimizer/bulk-images/${jobId}/cancel`);
      setJobData((p) => ({ ...p, status: "cancelled" }));
      toast.info("تم إيقاف العملية");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر الإيقاف");
    }
  };

  const isRunning   = ["running", "starting"].includes(jobData?.status);
  const isCompleted = jobData?.status === "completed";
  const isFailed    = jobData?.status === "failed";
  const isCancelled = jobData?.status === "cancelled";

  const liveProducts = jobData?.live_products || [];

  return (
    <div className="space-y-4" data-testid="bulk-image-optimizer">

      {/* Main Card */}
      <div className="bg-white rounded-2xl border overflow-hidden shadow-sm transition-all"
        style={{ borderColor: isCompleted ? "#A7F3D0" : isRunning ? "#BFDBFE" : "#E2E8F0" }}>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b"
          style={{ background: isCompleted ? "#ECFDF5" : isRunning ? "#EFF6FF" : "#F8FAFC", borderColor: isCompleted ? "#A7F3D0" : isRunning ? "#BFDBFE" : "#E2E8F0" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border"
            style={{ background: isCompleted ? "#D1FAE5" : "#DBEAFE", borderColor: isCompleted ? "#A7F3D0" : "#BFDBFE" }}>
            {isRunning
              ? <div className="w-5 h-5 border-2 border-[#004EEB] border-t-transparent rounded-full animate-spin" />
              : <Image className="w-5 h-5" style={{ color: isCompleted ? "#059669" : "#004EEB" }} />
            }
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-slate-800">تحسين صور المنتجات — تلقائي 100%</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {isRunning ? `معالجة ${jobData.processed || 0} من ${jobData.total || "?"} منتج...`
               : isCompleted ? `اكتمل — ضُغطت ${jobData.compressed_count} صورة، وُفِّر ${fmtBytes(jobData.saved_bytes)}`
               : "تحميل + ضغط + رفع صور WebP لجميع المنتجات"}
            </div>
          </div>
          <span className="text-[9px] font-bold px-2.5 py-1 rounded-full border shrink-0"
            style={{
              background: isCompleted ? "#D1FAE5" : isRunning ? "#DBEAFE" : "#F1F5F9",
              color: isCompleted ? "#059669" : isRunning ? "#004EEB" : "#64748B",
              borderColor: isCompleted ? "#A7F3D0" : isRunning ? "#BFDBFE" : "#E2E8F0",
            }}>
            {isCompleted ? "مكتمل" : isRunning ? "جارٍ..." : "تلقائي"}
          </span>
        </div>

        <div className="p-5 space-y-4">
          {/* No Salla connection warning */}
          {!sallaConnected && (
            <div className="flex items-start gap-2 p-3 rounded-xl border" style={{ background: "#FFFBEB", borderColor: "#FDE68A" }}>
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-700">
                يلزم ربط متجر سلة أولاً.{" "}
                <a href="/settings" className="underline font-bold">اذهب للإعدادات</a>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {jobData && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-700">
                  {isRunning ? "جاري التحسين..." : isCompleted ? "اكتمل التحسين" : isFailed ? "فشلت العملية" : isCancelled ? "تم الإيقاف" : ""}
                </span>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="font-mono font-bold">{jobData.progress || 0}%</span>
                  {isRunning && jobData.total > 0 && (
                    <span>{jobData.processed || 0} / {jobData.total}</span>
                  )}
                </div>
              </div>
              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${jobData.progress || 0}%`,
                    background: isCompleted ? "#10B981" : isFailed ? "#EF4444" : "linear-gradient(to left, #004EEB, #60a5fa)"
                  }} />
              </div>

              {/* Stats Row */}
              {(jobData.compressed_count > 0 || jobData.processed > 0) && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {[
                    { label: "مضغوطة", val: jobData.compressed_count || 0, color: "#10B981", bg: "#ECFDF5" },
                    { label: "فُحصت",  val: `${jobData.processed || 0}/${jobData.total || "?"}`, color: "#004EEB", bg: "#EFF6FF" },
                    { label: "التوفير", val: fmtBytes(jobData.saved_bytes || 0), color: "#8B5CF6", bg: "#F5F3FF" },
                  ].map((m) => (
                    <div key={m.label} className="text-center rounded-xl p-2.5" style={{ background: m.bg }}>
                      <div className="text-sm font-bold font-mono" style={{ color: m.color }}>{m.val}</div>
                      <div className="text-[9px] text-slate-500 mt-0.5">{m.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error message */}
              {isFailed && jobData.error && (
                <div className="mt-2 text-xs rounded-xl p-3 border" style={{ color: "#B91C1C", background: "#FEF2F2", borderColor: "#FECACA" }}>
                  {jobData.error}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap">
            {!isRunning ? (
              <>
                {sallaConnected && !starting && (
                  <button onClick={() => setShowPreview(!showPreview)} data-testid="preview-products-btn"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all border"
                    style={{ background: showPreview ? "#EFF6FF" : "#F8FAFC", color: "#004EEB", borderColor: "#BFDBFE" }}>
                    <Eye className="w-3.5 h-3.5" />
                    {showPreview ? "إخفاء المعاينة" : "معاينة المنتجات"}
                  </button>
                )}
                <button onClick={startJob} disabled={!sallaConnected || starting} data-testid="start-bulk-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:shadow-lg hover:shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: "#004EEB" }}>
                  {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                  {starting ? "جارٍ البدء..." : isCompleted ? "تشغيل مرة أخرى" : "ابدأ التحسين الآن"}
                </button>
              </>
            ) : (
              <button onClick={cancelJob} data-testid="cancel-bulk-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors border"
                style={{ background: "#FEF2F2", color: "#B91C1C", borderColor: "#FECACA" }}>
                <Square className="w-3.5 h-3.5" /> إيقاف العملية
              </button>
            )}
            <button onClick={loadActions} title="تحديث"
              className="px-3 py-2.5 rounded-xl border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-[var(--bg-card-hover)] transition-colors">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Disclaimer */}
          <p className="text-[10px] text-slate-400 leading-relaxed">
            ستُرفع صور WebP مضغوطة كصور رئيسية للمنتجات. الصور القديمة تبقى في المعرض. التوفير المتوقع: 40-80%.
          </p>
        </div>
      </div>

      {/* Product Preview Panel */}
      {showPreview && sallaConnected && (
        <ProductPreviewList
          onConfirm={startJob}
          onClose={() => setShowPreview(false)}
          sallaConnected={sallaConnected}
        />
      )}

      {/* Live Products Feed */}
      {isRunning && liveProducts.length > 0 && (
        <div className="bg-white rounded-2xl border border-blue-100 overflow-hidden shadow-sm" data-testid="live-products-feed">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-blue-100" style={{ background: "#EFF6FF" }}>
            <div className="w-2 h-2 rounded-full bg-[#004EEB] animate-pulse" />
            <span className="text-xs font-bold text-[#004EEB]">تحديث لحظي — المنتجات الأخيرة</span>
            <span className="ms-auto text-[10px] text-blue-400">{liveProducts.length} منتج</span>
          </div>
          <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
            {[...liveProducts].reverse().map((p, i) => (
              <LiveProductCard key={`${p.product_id}-${i}`} product={p} />
            ))}
          </div>
        </div>
      )}

      {/* Completed products summary */}
      {isCompleted && liveProducts.length > 0 && (
        <div className="bg-white rounded-2xl border border-emerald-100 overflow-hidden shadow-sm">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-emerald-100" style={{ background: "#ECFDF5" }}>
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-bold text-emerald-700">المنتجات المُحسَّنة</span>
            <span className="ms-auto text-[10px] text-emerald-500">{liveProducts.filter(p => p.status === "compressed").length} صورة</span>
          </div>
          <div className="p-4 space-y-2 max-h-60 overflow-y-auto">
            {liveProducts.filter(p => p.status === "compressed").slice(-20).reverse().map((p, i) => (
              <LiveProductCard key={`${p.product_id}-done-${i}`} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Manual Action ── */
function ManualAction({ icon: Icon, title, description, steps, impact }) {
  const [open, setOpen] = useState(false);
  const col = impact === "high" ? "#EF4444" : impact === "medium" ? "#F59E0B" : "#6B7280";
  const bg  = impact === "high" ? "#FEF2F2" : impact === "medium" ? "#FFFBEB" : "#F8FAFC";
  const label = impact === "high" ? "تأثير عالٍ" : impact === "medium" ? "تأثير متوسط" : "تأثير منخفض";

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-slate-300 transition-all">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-4 text-right hover:bg-[var(--bg-card-hover)] transition-colors">
        <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-slate-500" />
        </div>
        <div className="flex-1 text-right">
          <div className="text-sm font-bold text-slate-800">{title}</div>
          <div className="text-xs text-slate-400 mt-0.5">{description}</div>
        </div>
        <span className="text-[9px] font-bold px-2 py-1 rounded-full shrink-0" style={{ background: bg, color: col }}>{label}</span>
        <span className="text-[9px] font-bold px-2 py-1 rounded-full bg-slate-50 text-slate-500 border border-slate-100 shrink-0">يدوي</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div className="border-t border-slate-100 px-5 py-4 bg-slate-50">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">خطوات التنفيذ</div>
          <ol className="space-y-2">
            {steps.map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                <span className="leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   NAMED EXPORTS (used by StoreOptimizer tabs)
═══════════════════════════════════════════════════ */
export { ScriptPanel, ManualAction, BulkImageOptimizer };

/* ═══════════════════════════════════════════════════
   DEFAULT EXPORT (auto-section only)
═══════════════════════════════════════════════════ */
export default function OptimizationEngine({ sallaConnected = false }) {
  return <BulkImageOptimizer sallaConnected={sallaConnected} />;
}
